import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const CATEGORY_ORDER = [
  'Triggers',
  'Agents',
  'AI',
  'Communication',
  'Data',
  'HTTP',
  'Logic',
  'Utilities',
];

const CATEGORY_COLORS = {
  Triggers: '#10B981',
  Agents:   '#F97316',
  AI: '#8B5CF6',
  Communication: '#F59E0B',
  Data: '#3B82F6',
  HTTP: '#06B6D4',
  Logic: '#EF4444',
  Utilities: '#64748B',
};

function DynamicIcon({ name, size = 14, color }) {
  const Icon = LucideIcons[name];
  return Icon ? <Icon size={size} color={color} /> : null;
}

export default function NodeSidebar({ nodeTypes, onClose }) {
  const { isDark } = useTheme();
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [width, setWidth] = useState(250);
  const isResizing = useRef(false);

  // Surface and text colors based on theme
  const bg = isDark ? '#0d1117' : '#f8fafc';
  const border = isDark ? '#1F2937' : '#e2e8f0';
  const text1 = isDark ? '#E6EDF3' : '#0f172a';
  const text2 = isDark ? '#64748B' : '#64748b';
  const inputBg = isDark ? '#161B22' : '#ffffff';
  const inputBorder = isDark ? '#30363D' : '#cbd5e1';
  const cardBg = isDark ? '#0d1117' : '#ffffff';
  const cardHoverBorder = isDark ? '#8B5CF6' : '#8B5CF6';

  const grouped = useMemo(() => {
    const filtered = nodeTypes.filter(
      (n) =>
        !search ||
        n.label.toLowerCase().includes(search.toLowerCase()) ||
        n.description.toLowerCase().includes(search.toLowerCase())
    );

    const groups = {};
    for (const n of filtered) {
      if (!groups[n.category]) groups[n.category] = [];
      groups[n.category].push(n);
    }
    return groups;
  }, [nodeTypes, search]);

  const toggleCategory = (cat) => {
    setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const onDragStart = (e, node) => {
    e.dataTransfer.setData(
      'application/reactflow',
      JSON.stringify({ type: node.type, label: node.label })
    );
    e.dataTransfer.effectAllowed = 'move';
  };

  const startResizing = (e) => {
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    e.preventDefault();
  };

  const stopResizing = () => {
    isResizing.current = false;
    document.body.style.cursor = 'default';
  };

  const resize = (e) => {
    if (isResizing.current) {
      const newWidth = e.clientX;
      if (newWidth > 200 && newWidth < 450) {
        setWidth(newWidth);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, []);

  const orderedCategories = CATEGORY_ORDER.filter((c) => grouped[c]);

  return (
    <div style={{
      width,
      flexShrink: 0,
      background: bg,
      borderRight: `1px solid ${border}`,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      userSelect: isResizing.current ? 'none' : 'auto',
      transition: isResizing.current ? 'none' : 'width 0.1s ease',
      height: '100%',
    }}>
      {/* Resizer Handle */}
      <div 
        onMouseDown={startResizing}
        style={{
          position: 'absolute',
          top: 0,
          right: -3,
          width: 6,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 10,
        }}
        title="Drag to resize"
      />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px 8px', borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: text1, letterSpacing: '0.02em' }}>Nodes</span>
        <span style={{ fontSize: 10, color: text2, background: inputBg, padding: '2px 7px', borderRadius: 100, border: `1px solid ${inputBorder}` }}>{nodeTypes.length} types</span>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', padding: '10px 12px', flexShrink: 0 }}>
        <Search size={13} color={text2} style={{ position: 'absolute', left: 22, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          style={{
            width: '100%', background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 8,
            padding: '6px 8px 6px 30px', color: text1, fontSize: 12, outline: 'none', boxSizing: 'border-box',
          }}
          placeholder="Search nodes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Node groups */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 8px' }}>
        {orderedCategories.map((category) => {
          const nodes = grouped[category] || [];
          const isOpen = !collapsed[category];
          const color = CATEGORY_COLORS[category] || '#94A3B8';

          return (
            <div key={category} style={{ borderBottom: `1px solid ${isDark ? '#161B22' : '#f1f5f9'}` }}>
              <button
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                  padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                }}
                onClick={() => toggleCategory(category)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: `0 0 6px ${color}80` }} />
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color }}>{category}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 10, color: text2, background: inputBg, padding: '1px 5px', borderRadius: 4 }}>{nodes.length}</span>
                  {isOpen ? <ChevronDown size={12} color={text2} /> : <ChevronRight size={12} color={text2} />}
                </div>
              </button>

              {/* Node cards */}
              {isOpen && (
                <div style={{ padding: '2px 8px 8px' }}>
                  {nodes.map((node) => (
                    <div
                      key={node.type}
                      className="node-card"
                      draggable
                      onDragStart={(e) => onDragStart(e, node)}
                      title={node.description}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 8px',
                        background: cardBg, border: `1px solid ${border}`, borderRadius: 8,
                        marginBottom: 4, cursor: 'grab', transition: 'all 0.15s ease', userSelect: 'none',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = cardHoverBorder}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = border}
                    >
                      <div style={{ width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, background: `${color}18`, border: `1px solid ${color}25` }}>
                        <DynamicIcon name={node.icon} size={13} color={color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: text1, marginBottom: 1 }}>{node.label}</div>
                        <div style={{ fontSize: 10, color: text2, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {node.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {orderedCategories.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: text2, fontSize: 12 }}>No nodes match "{search}"</div>
        )}
      </div>

      {/* Footer hint */}
      <div style={{ padding: '8px 12px', borderTop: `1px solid ${border}`, flexShrink: 0 }}>
        <span style={{ fontSize: 10, color: text2 }}>↕ Drag nodes onto the canvas</span>
      </div>
    </div>
  );
}
